import { inject, Injectable } from '@angular/core';
import { FirebaseAnalyticsService } from './firebase.analytics.service';
import { LocalStorageService } from '../core/localStorageService';
import { ProjectDto, UserDto } from '../core/mceapi.client.generated';
import { isSome, match, Option } from '../shared/functions/functional';

export type FirebaseEvent =
  | { name: 'sign_out'; params: { method: string; }; }
  | { name: 'sign_in'; params: { method: string; }; }
  | { name: 'change_email'; params: {}; }
  | { name: 'shared_billing_invitation_accepted'; params: {}; }
  | { name: 'shared_billing_invitation_declined'; params: {}; }
  | { name: 'unregister-user'; params: {}; }
  | { name: 'add_project'; params: { projectUid: string | null; }; }
  | { name: 'delete_project'; params: { projectUid: string; }; }
  | { name: 'edit_project'; params: { projectUid: string; }; }
  | { name: 'copy_project'; params: { projectUid: string; toProjectUid: string; }; }
  | { name: 'add_project_user'; params: { projectUid: string; projectUserUid: string; }; }
  | { name: 'remove_project_user'; params: { projectUid: string; projectUserUid: string; }; }
  | { name: 'add_cable'; params: { cableUid: string; projectUid: string; }; }
  | { name: 'delete_cable'; params: { cableUid: string; projectUid: string; }; }
  | { name: 'edit_cable'; params: { cableUid: string; projectUid: string; }; }
  | { name: 'edit_cable_started'; params: { cableUid: string; projectUid: string; }; }
  | { name: 'copy_cable'; params: { cableUid: string; toCableUid: string; projectUid: string; }; }
  | { name: 'move_cable'; params: { cableUid: string; toProjectUid: string; }; }
  | { name: 'cable-select-manufacturer'; params: {cableUid:string, manufacturerUid: string; }; }
  | { name: 'report'; params: { reportName: string; }; }


@Injectable({
  providedIn: 'root',
})
export class FirebaseEventService {

  private readonly analytics = inject(FirebaseAnalyticsService);
  private readonly localStorage = inject(LocalStorageService);

  private sendEvent(event: FirebaseEvent, user?: UserDto): void {
    const browserUid = this.localStorage.browserUid();
    const params = user
      ? { ...event.params, userUID: user.uid, browserUID: browserUid }
      : { ...event.params, browserUID: browserUid };

    try {
      this.analytics.logEvent(event.name, params);
    } catch (error) {
      console.error(`Error logging event '${event.name}':`, error);
    }
  }

  private logUserOptEvent(userOpt: Option<UserDto>, event: FirebaseEvent): void {
    match(
      () => this.sendEvent(event),
      (user: UserDto) => this.sendEvent(event, user))
      (userOpt);
  }

  logUnregisterUserEvent = (user: Option<UserDto>) =>
    this.logUserOptEvent(user, { name: 'unregister-user', params: {} });

  logSignInEvent = (method: string) =>
    this.sendEvent({ name: 'sign_in', params: { method } });

  logChangeEmailEvent = (user: Option<UserDto>) =>
    this.logUserOptEvent(user, { name: 'change_email', params: {} });

  logSharedBillingInvitationAcceptedEvent = (user: Option<UserDto>) =>
    this.logUserOptEvent(user, { name: 'shared_billing_invitation_accepted', params: {} });

  logSharedBillingInvitationDeclinedEvent = (user: Option<UserDto>) =>
    this.logUserOptEvent(user, { name: 'shared_billing_invitation_declined', params: {} });

  logAddProjectEvent = (user: Option<UserDto>, project: ProjectDto) => {
    const projectUid = project.uid;
    if (!projectUid) return;

    this.logUserOptEvent(user, {
      name: 'add_project',
      params: { projectUid }
    });
  };

  logDeleteProjectEvent = (user: Option<UserDto>, project: ProjectDto) => {
    const projectUid = project.uid;
    if (!projectUid) return;

    this.logUserOptEvent(user, { name: 'delete_project', params: { projectUid } });
  };

  logSaveProjectEvent = (user: Option<UserDto>, project: ProjectDto) => {
    const projectUid = project.uid;
    if (!projectUid) return;

    this.logUserOptEvent(user, { name: 'edit_project', params: { projectUid } });
  };

  logCopyProjectEvent = (user: Option<UserDto>, project: ProjectDto, toProject: ProjectDto) => {
    const projectUid = project.uid;
    const toProjectUid = toProject.uid;
    if (!projectUid || !toProjectUid) return;

    this.logUserOptEvent(user, { name: 'copy_project', params: { projectUid, toProjectUid } });
  };

  logAddProjectUserEvent = (user: Option<UserDto>, project: ProjectDto, userToAdd: UserDto) => {
    const projectUid = project.uid;
    const projectUserUid = userToAdd.uid;
    if (!projectUid || !projectUserUid) return;

    this.logUserOptEvent(user, { name: 'add_project_user', params: { projectUid, projectUserUid } });
  };

  logRemoveProjectUserEvent = (user: Option<UserDto>, project: ProjectDto, userToRemove: UserDto) => {
    const projectUid = project.uid;
    const projectUserUid = userToRemove.uid;
    if (!projectUid || !projectUserUid) return;

    this.logUserOptEvent(user, { name: 'remove_project_user', params: { projectUid, projectUserUid } });
  };

  logAddCableEvent = (user: Option<UserDto>, projectUid: string, cableUid: string) =>
    this.logUserOptEvent(user, { name: 'add_cable', params: { projectUid, cableUid } });

  logDeleteCableEvent = (user: Option<UserDto>, projectUid: string, cableUid: string) =>
    this.logUserOptEvent(user, { name: 'delete_cable', params: { projectUid, cableUid } });

  logSaveCableEvent = (user: Option<UserDto>, projectUid: string, cableUid: string) =>
    this.logUserOptEvent(user, { name: 'edit_cable', params: { projectUid, cableUid } });

  logEditCableStartedEvent = (user: Option<UserDto>, projectUid: string, cableUid: string) =>
    this.logUserOptEvent(user, { name: 'edit_cable_started', params: { projectUid, cableUid } });

  logCopyCableEvent = (user: Option<UserDto>, projectUid: string, cableUid: string, toCableUid: string) =>
    this.logUserOptEvent(user, { name: 'copy_cable', params: { projectUid, cableUid, toCableUid } });

  logMoveCableEvent = (user: Option<UserDto>, cableUid: string, toProjectUid: string) =>
    this.logUserOptEvent(user, { name: 'move_cable', params: { cableUid, toProjectUid } });

  logCableSelectManufacturerEvent = (user: Option<UserDto>, cableUid: string, manufacturerUid: string) =>
    this.logUserOptEvent(user, { name: 'cable-select-manufacturer', params: { cableUid, manufacturerUid } });

  logReportEvent = (user: Option<UserDto>, reportName: string) =>
    this.logUserOptEvent(user, { name: 'report', params: { reportName } });


}


